import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty, cloneDeep } from 'lodash';
import '@/components/member/member.scss';
import {
  setCurrentSelectedPhone,
  setMemberEditEntrance,
  setMemberPageState,
} from '@/actions/member/memberAction';
import { ReactComponent as Warn } from '@/assets/img/warn.svg';
import PrimaryButton from '@/ui/PrimaryButton';
import CustomizeDatePicker from '@/ui/CustomizeDatePicker';
import CustomizeSelect from '@/ui/CustomizeSelect';
import UpdateProfileSuccessModal from '@components/member/components/updateProfileSuccessModal';
import MemberHeader from '@components/member/components/memberHeader';
import RemoveMemberModal from '@components/member/components/removeMemberModal';
import { Style } from '@/components/style';
import {
  deleteMember,
  getMemberInfo,
  setIsLogin,
  setMemberInfoPhone,
  updateMemberInfo,
} from '@actions/memberInfo/memberInfoAction';
import PropTypes from 'prop-types';
import moment from 'moment';
import RemoveMemberSuccessModal from '@components/member/components/removeMemberSuccessModal';
import { setCartCustomerName } from '@actions/cartV2/cartV2Action';
import {
  setWelcomeCustomerName,
  setWelcomeCustomerPhone,
} from '@actions/customizeWelcome/customizeWelcomeAction';
import AreaCodeSelector from '@/ui/AreaCodeSelector';
import Area from '@/utils/area';
import formatTWPhone from '@/utils/phoneUtils/formatTWPhone';

const { alertColor, blackColor, secondaryColor, gray3Color } =
  new Style().getUiConfig();

function MemberProfile({ storeUuid }) {
  const intl = useIntl();
  const dispatch = useDispatch<any>();

  // modal controller -start-
  const [showUpdateSuccessModal, setShowUpdateSuccessModal] = useState(false);
  const handleUpdateSuccessModalClose = () => setShowUpdateSuccessModal(false);
  const handleUpdateSuccessModalShow = () => setShowUpdateSuccessModal(true);

  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const handleRemoveModalModalClose = () => setShowRemoveMemberModal(false);
  const handleRemoveModalModalShow = () => setShowRemoveMemberModal(true);

  const [showRemoveMemberSuccessModal, setShowRemoveMemberSuccessModal] =
    useState(false);
  const handleRemoveModalSuccessModalClose = () =>
    setShowRemoveMemberSuccessModal(false);
  const handleRemoveModalSuccessModalShow = () =>
    setShowRemoveMemberSuccessModal(true);
  // modal controller -end-

  // name -start-
  const [isNameValid, setNameValid] = useState(true);
  const [name, setName] = useState('');

  const onChangeName = (e) => {
    setName(e.target.value);
  };

  // 最長不得超過7個漢字，或14個位元組(數字，字母和底線)正規表示式
  const checkNameRegExp = (memberName) => {
    const pattern = /^([\w\u4e00-\u9fa5_]* *[\w\u4e00-\u9fa5_]*)+$/;
    const nameRegexp = new RegExp(pattern);
    return nameRegexp.test(memberName);
  };

  useEffect(() => {
    if (isEmpty(name)) {
      setNameValid(true);
      return;
    }

    setNameValid(checkNameRegExp(name));
  }, [name]);
  // name -end-

  // phone -start-
  const [areaCodes, setAreaCodes] = useState<any>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<any>(null);
  const [phone, setPhone] = useState<string>('');
  const memberLangs = useSelector(
    (state: RootStateOrAny) => state.memberReducer.memberLangs
  );

  useEffect(() => {
    if (isEmpty(memberLangs)) return;

    const tempAreaCodes = Area.getAreaCodesListByLangs(memberLangs);
    setAreaCodes(tempAreaCodes);
    setSelectedAreaCode(tempAreaCodes[0]);
  }, [memberLangs]);
  // phone -end-

  // birthday -start-
  const [memberBirthDate, setMemberBirthDate] = useState<any>(null);
  const maxDate = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
  };
  // birthday -end-

  // member gender -start-
  const genderOptions = [
    {
      value: 'MALE',
      label: intl.formatMessage({ id: 'member.profile.gender.MALE' }),
    },
    {
      value: 'FEMALE',
      label: intl.formatMessage({ id: 'member.profile.gender.FEMALE' }),
    },
  ];
  const [memberGender, setMemberGender] = useState('DEFAULT');
  // member gender -end-

  // init member profile
  const { areaCode, phone: memberPhone } = useSelector<RootStateOrAny, any>(
    (state) => state.memberInfoReducer
  );
  const [memberProfileData, setMemberProfileData] = useState({
    memberInfoId: '',
    name: '',
    areaCode,
    phone: memberPhone,
    birthday: '',
    gender: 'DEFAULT',
  });

  const pageState = useSelector(
    (state: RootStateOrAny) => state.memberReducer.memberPageState
  );

  const initMemberInfo = async () => {
    const memberInfo = await dispatch(getMemberInfo(storeUuid));

    setMemberProfileData({
      ...memberProfileData,
      ...memberInfo,
    });
  };

  useEffect(() => {
    initMemberInfo();
  }, []);

  const disabledAllPhoneElements = () => {
    const allElements: any = document.getElementsByTagName('*');
    for (const item of allElements) {
      if (item.id == 'phone') item.disabled = true;
      if (item.id == 'areaCodeDropDownMenu') item.disabled = true;
    }
  };

  const enabledAllPhoneElements = () => {
    const allElements: any = document.getElementsByTagName('*');
    for (const item of allElements) {
      if (item.id == 'phone') item.disabled = false;
      if (item.id == 'areaCodeDropDownMenu') item.disabled = false;
    }
  };

  useEffect(() => {
    const isPhoneDisabled = sessionStorage.getItem('memberEditDisabled');

    if (pageState === 'MemberProfile' && isPhoneDisabled === 'true') {
      disabledAllPhoneElements();
    }
  }, [pageState]);

  useEffect(() => {
    if (isEmpty(memberProfileData)) return;

    if (memberProfileData?.name) {
      setName(memberProfileData.name);
    }

    if (memberProfileData?.phone) {
      // 會員 手機 格式替換 +886 => 09
      setPhone(formatTWPhone(memberProfileData.phone));
    }

    if (memberProfileData?.birthday) {
      const tempDate = new Date(memberProfileData.birthday);

      setMemberBirthDate(tempDate);
    }
    if (memberProfileData?.gender) {
      setMemberGender(memberProfileData.gender);
    }
  }, [memberProfileData]);

  // 是否註冊過
  const submit = async () => {
    // check profile data is valid
    // check name valid
    if (!isEmpty(name)) {
      setNameValid(checkNameRegExp(name));
    } else {
      setNameValid(true);
    }

    if (!isNameValid) return;

    let birthday: string | null = null;
    if (memberBirthDate) {
      birthday = moment(memberBirthDate).format('YYYY-MM-DD');
    }
    const res = await dispatch(
      updateMemberInfo({
        name,
        birthday,
        gender: memberGender,
      })
    );

    dispatch(setCartCustomerName(name));
    dispatch(setWelcomeCustomerName(name));

    if (res.code === '' || res.code === '0') {
      handleUpdateSuccessModalShow();
    }
  };

  const deleteMemberInfo = async () => {
    await dispatch(deleteMember());
    dispatch(setIsLogin(false));

    handleRemoveModalModalClose();
    handleRemoveModalSuccessModalShow();
  };

  const deleteMemberSuccess = () => {
    dispatch(setMemberPageState(''));
    dispatch(setMemberEditEntrance(false));
    dispatch(setCurrentSelectedPhone(''));
    dispatch(setMemberInfoPhone(''));
    dispatch(setWelcomeCustomerPhone(''));
    enabledAllPhoneElements();
    handleRemoveModalSuccessModalClose();
  };

  return (
    <div>
      <MemberHeader
        title={intl.formatMessage({
          id: 'member.header.title.MemberProfile',
        })}
      >
        <div
          className="fw-normal"
          style={{
            color: secondaryColor,
            fontSize: '12px',
            marginLeft: '15px',
          }}
          onClick={() => {
            handleRemoveModalModalShow();
          }}
        >
          <FormattedMessage id="member.header.remove.account" />
        </div>
      </MemberHeader>

      <div className="container-member">
        <main>
          <div className="d-grid gap-2 member-form-layout border-bottom border-1">
            {/* 姓名 */}
            <div className="d-flex align-items-center justify-content-around mb-3">
              <div
                className={`d-flex align-items-center ${
                  isNameValid ? 'pb-0' : 'pb-3'
                }`}
              >
                <FormattedMessage id="member.profile.title.name" />
              </div>

              <div className="member-input">
                <input
                  style={{
                    padding: '10px',
                    width: '100%',
                    borderRadius: '10px',
                    border: `1px solid ${gray3Color}`,
                    color: `${blackColor}`,
                  }}
                  data-testid="name"
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'member.profile.title.name.placeholder',
                  })}
                  value={name}
                  onChange={onChangeName}
                />
                {!isNameValid && (
                  <div
                    className="d-flex align-items-center"
                    style={{
                      color: alertColor,
                      fontSize: '12px',
                      minHeight: '1em',
                    }}
                  >
                    <Warn style={{ marginRight: '5px' }} />
                    {intl.formatMessage({
                      id: 'member.profile.name.warn',
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* 手機 */}
            <div className="d-flex align-items-center justify-content-around mb-3">
              <div className="d-flex align-items-center">
                <FormattedMessage id="member.profile.title.phone" />
              </div>
              <div className="member-input">
                <div className="gap-2 w-100 d-flex justify-content-between phone-edit-position-relative">
                  <AreaCodeSelector
                    setAreaCode={setSelectedAreaCode}
                    areaCode={selectedAreaCode}
                    areaCodeList={areaCodes}
                    disabled
                  />
                  <input
                    className="phone"
                    style={{
                      border: `1px solid ${gray3Color}`,
                      color: '#808080',
                      padding: '10px',
                    }}
                    type="tel"
                    id="phone"
                    data-testid="phone"
                    placeholder={intl.formatMessage({
                      id: 'cart.customerInfo.phone.required',
                    })}
                    defaultValue={phone}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="d-grid gap-2 member-form-layout border-bottom border-1">
            {/* 生日 */}
            <div className="d-flex align-items-center justify-content-around">
              <div>
                <FormattedMessage id="member.profile.title.birth" />
              </div>
              <div className="member-input">
                <CustomizeDatePicker
                  value={memberBirthDate}
                  onChange={setMemberBirthDate}
                  maxDate={maxDate}
                />
              </div>
            </div>

            {/* 性別 */}
            <div className="d-flex align-items-center justify-content-around ">
              <div>
                <FormattedMessage id="member.profile.title.gender" />
              </div>
              <div className="position-relative member-input">
                <CustomizeSelect
                  value={memberGender}
                  option={genderOptions}
                  onChange={setMemberGender}
                />
              </div>
            </div>
          </div>

          <div className="member-form-footer">
            <PrimaryButton type="" onClick={submit}>
              <div className="fw-bolder" style={{ fontSize: '18px' }}>
                <FormattedMessage id="common.btn.save" />
              </div>
            </PrimaryButton>
          </div>
        </main>
      </div>

      <UpdateProfileSuccessModal
        show={showUpdateSuccessModal}
        handleClose={handleUpdateSuccessModalClose}
        confirmClick={handleUpdateSuccessModalClose}
      />

      <RemoveMemberModal
        show={showRemoveMemberModal}
        handleClose={handleRemoveModalModalClose}
        confirmClick={deleteMemberInfo}
      />
      <RemoveMemberSuccessModal
        show={showRemoveMemberSuccessModal}
        handleClose={handleRemoveModalSuccessModalClose}
        confirmClick={deleteMemberSuccess}
      />
    </div>
  );
}

MemberProfile.defaultProps = {};

MemberProfile.propTypes = {
  storeUuid: PropTypes.string.isRequired,
};

export default MemberProfile;
